import {
  IonRow,
  IonCol,
  IonContent,
  IonButton,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonIcon,
  IonAlert,
  useIonViewWillEnter,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import {
  add,
  calendarOutline,
  clipboardOutline,
  heartOutline,
  personCircleOutline,
} from 'ionicons/icons';
import { useAuth } from '../../../auth';
import { useHistory } from 'react-router-dom';
import { useTrainingStore } from '../../../stores/useTrainingStore';
import { fitnessProfileRepository, workoutRepository } from '../../../repositories/LocalRepositories';

import './Training.css';

const Training: React.FC = () => {
  const { email, userId } = useAuth();
  const history = useHistory();
  const sessionId = useTrainingStore(state => state.sessionId);
  const hydrateFromStorage = useTrainingStore(state => state.hydrateFromStorage);

  const [showStartPrompt, setShowStartPrompt] = useState(false);
  const [showCompletedState, setShowCompletedState] = useState(false);
  const [nickname, setNickname] = useState('');

  const refreshCompletedState = async () => {
    if (!userId) {
      setShowCompletedState(false);
      return;
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

    try {
      const sessions = await workoutRepository.list(userId);
      const hasCompletedWorkout = sessions.some(session => {
        const started = session.start_date.toMillis();
        return started >= startOfToday.getTime() && started < startOfTomorrow.getTime() && session.end_date != null;
      });

      setShowCompletedState(hasCompletedWorkout);

      // This flag was used by the previous implementation. Removing it keeps
      // deleted sessions from leaving stale completion state behind.
      localStorage.removeItem('lastCompletedWorkoutDate');
    } catch (error) {
      console.error('Could not check today\'s completed workouts:', error);
      setShowCompletedState(false);
    }
  };

  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  useIonViewWillEnter(() => {
    void refreshCompletedState();
    if (userId) fitnessProfileRepository.get(userId).then(profile => setNickname(profile?.displayName?.trim() || ''));
  });

  useEffect(() => {
    void refreshCompletedState();
  }, [userId]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--ploppy-training-alert-image',
      'url("/assets/ploppy/ploppy-training.png")'
    );

    return () => {
      document.documentElement.style.removeProperty(
        '--ploppy-training-alert-image'
      );
    };
  }, []);

  const name = nickname || (email && email.includes('@') ? email.split('@')[0] : '') || 'Guest';

  const handleNewWorkoutClick = () => {
    const activeSessionId = sessionId || localStorage.getItem('sessionId');

    if (activeSessionId) {
      history.push('/my/trainingstart');
      return;
    }

    setShowStartPrompt(true);
  };

  const handleConfirmStart = () => {
    setShowStartPrompt(false);
    history.push('/my/trainingstart?autostart=1');
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img
                src="/assets/pl_icon/icon_nf_matt.png"
                alt="Pawer Lifting"
                style={{ height: 50, width: 'auto', display: 'block' }}
              />
              <IonTitle size="small" style={{ padding: 0 }}>
                Training
              </IonTitle>
            </div>

            <IonButton
              shape="round"
              fill="clear"
              aria-label="Account"
              routerLink="/my/settings"
            >
              <IonIcon icon={personCircleOutline} slot="icon-only" />
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="training-content">
        <IonRow className="ion-padding training-hero-row">
          <IonCol size="12">
            <IonCard className="welcome-card ploppy-hero">
              <IonCardContent className="ploppy-hero__content">
                <div className="ploppy-hero__mascot" aria-hidden="true">
                  <img
                    src={
                      showCompletedState
                        ? '/assets/ploppy/ploppy-finished.png'
                        : '/assets/ploppy/ploppy.png'
                    }
                    alt=""
                    className="ploppy-hero__image"
                  />
                </div>

                <div className="ploppy-hero__message">
                  <div className="ploppy-hero__eyebrow">Hi, {name}!</div>
                  <h1>{showCompletedState ? 'You made it!' : 'Ready to move?'}</h1>
                  <p>
                    {showCompletedState
                      ? 'Ploppy is happy to see you moving toward your goals.'
                      : "Ploppy is here to cheer you through today's workout."}
                  </p>
                </div>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="ion-padding" style={{ paddingTop: 0 }}>
          <IonCol size="6">
            <IonCard
              className="quick-card"
              button
              onClick={handleNewWorkoutClick}
            >
              <IonCardContent className="quick-card__content">
                <div className="quick-card__text">
                  <div className="quick-card__title">New workout</div>
                </div>

                <IonButton
                  shape="round"
                  fill="solid"
                  color="primary"
                  className="quick-card__roundbtn"
                  aria-label="New workout"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNewWorkoutClick();
                  }}
                >
                  <IonIcon icon={add} slot="icon-only" />
                </IonButton>
              </IonCardContent>
            </IonCard>
          </IonCol>

          <IonCol size="6">
            <IonCard className="quick-card" button routerLink="/my/routines">
              <IonCardContent className="quick-card__content">
                <div className="quick-card__text">
                  <div className="quick-card__title">Routines</div>
                </div>

                <IonButton
                  shape="round"
                  fill="solid"
                  color="primary"
                  className="quick-card__roundbtn"
                  aria-label="Routines"
                >
                  <IonIcon icon={clipboardOutline} slot="icon-only" />
                </IonButton>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow className="ion-padding training-feed-row" style={{ paddingTop: 0 }}>
          <IonCol size="12">
            <section className="training-feed" aria-labelledby="training-feed-title">
              <div className="training-feed__heading">
                <div>
                  <span>Community</span>
                  <h2 id="training-feed-title">News &amp; inspiration</h2>
                </div>
                <span className="training-feed__static-label">Preview</span>
              </div>

              <div className="training-feed__cards">
                <IonCard className="training-news-card">
                  <div className="training-news-card__visual training-news-card__visual--lilac">
                    <IonIcon icon={heartOutline} />
                  </div>
                  <IonCardContent>
                    <div className="training-news-card__meta">@pawerlifting · Community</div>
                    <h3>Strong workouts, softer impact.</h3>
                    <p>We are preparing ways to support local animal shelters together.</p>
                  </IonCardContent>
                </IonCard>

                <IonCard className="training-news-card">
                  <div className="training-news-card__visual training-news-card__visual--warm">
                    <IonIcon icon={calendarOutline} />
                  </div>
                  <IonCardContent>
                    <div className="training-news-card__meta">@pawerlifting · Training tip</div>
                    <h3>Consistency beats intensity.</h3>
                    <p>A short workout still counts. Add the next one to your calendar.</p>
                  </IonCardContent>
                </IonCard>
              </div>
            </section>
          </IonCol>
        </IonRow>

        {/* Start prompt */}
        <IonAlert
          isOpen={showStartPrompt}
          onDidDismiss={() => setShowStartPrompt(false)}
          cssClass="workout-start-alert"
          header="Are you ready to pump?"
          message="Ploppy is ready when you are."
          buttons={[
            { text: 'No', role: 'cancel' },
            { text: 'Yes', handler: handleConfirmStart },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Training;
